import { useCallback, useState } from 'react';

export type ReportsViewMode = 'document' | 'focus';

const STORAGE_KEY = 'reports.viewMode';
const OUTLINE_COLLAPSED_KEY = 'reports.outlineCollapsed';

const readStoredMode = (): ReportsViewMode => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'focus' ? 'focus' : 'document';
  } catch {
    return 'document';
  }
};

const readStoredOutlineCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(OUTLINE_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

// Document = continuous scrollable doc; Focus = one section at a time
// (preserves the old view-by-section experience). The outline rail can be
// collapsed to a slim strip so the document gets the full pane width (split
// report+map layouts). Both persisted per browser.
export const useDocumentViewMode = () => {
  const [viewMode, setViewModeState] =
    useState<ReportsViewMode>(readStoredMode);
  const [outlineCollapsed, setOutlineCollapsedState] = useState<boolean>(
    readStoredOutlineCollapsed
  );

  const setViewMode = useCallback((mode: ReportsViewMode) => {
    setViewModeState(mode);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Storage unavailable (private mode etc.) — mode still works in-memory.
    }
  }, []);

  const setOutlineCollapsed = useCallback((collapsed: boolean) => {
    setOutlineCollapsedState(collapsed);
    try {
      window.localStorage.setItem(OUTLINE_COLLAPSED_KEY, String(collapsed));
    } catch {
      // Storage unavailable — collapse still works in-memory.
    }
  }, []);

  return { viewMode, setViewMode, outlineCollapsed, setOutlineCollapsed };
};
